/** @typedef {'en' | 'ru'} Locale */

/** @type {Record<Locale, Record<string, unknown>>} */
export const messages = {
  en: {
    common: {
      refresh: 'Refresh',
      view: 'View',
      premium: 'Premium',
      edit: 'Edit',
      delete: 'Delete',
      confirm: 'Confirm',
      cancel: 'Cancel',
      close: 'Close',
      save: 'Save',
      copyJson: 'Copy JSON',
      yes: 'Yes',
      no: 'No',
      all: 'All',
      signingIn: 'Signing in…',
      creating: 'Creating…',
      saving: 'Saving…',
      queueing: 'Queueing…',
      result: 'Result',
    },
    theme: { light: 'Light', dark: 'Dark' },
    language: { en: 'English', ru: 'Русский' },
    botLang: { all: 'All', uz: 'Uzbek', ru: 'Russian', en: 'English' },
    nav: {
      dashboard: 'Dashboard',
      agents: 'Agents',
      users: 'All Users',
      botUsers: 'Bot Users',
      providers: 'Providers',
      partners: 'Partners',
      subscriptions: 'Subscriptions',
      curatedTours: 'Tours',
      searchHistory: 'Search History',
      broadcast: 'Broadcast',
      admins: 'Admins',
      aiUsage: 'AI usage',
      language: 'Language',
      logout: 'Log out',
      partnerAnalytics: 'Partner analytics',
      sectionOverview: 'Overview',
      sectionPeople: 'People',
      sectionOperations: 'Operations',
      sectionSystem: 'System',
    },
    time: { justNow: 'just now', minAgo: '{n} min ago', hourAgo: '{n} h ago', dayAgo: '{n} d ago' },
    layout: { appName: 'UMA Admin' },
    login: {
      title: 'UMA Admin',
      subtitle: 'Sign in with admin credentials',
      username: 'Username',
      password: 'Password',
      signIn: 'Sign in',
      sideTitle: 'Everything UMA runs on, in one place',
      sideText: 'Agents, partners, operators, tours and Umrah packages.',
    },
    errors: { generic: 'Something went wrong' },
    notFound: {
      title: 'Page not found',
      body: 'The page you’re looking for doesn’t exist.',
      cta: 'Go to dashboard',
    },
    dashboard: {
      title: 'Dashboard',
      searches: 'Searches',
      searchersHint: '{n} users searched',
      newAccounts: 'New accounts',
      newAccountsHint: '{agents} agents · {bot} bot users',
      paidAccounts: 'Paid accounts',
      paidHint: '{trial} on trial · {revenue} received',
      aiCost: 'AI cost',
      aiHint: '{tokens} tokens',
      searchesPerDay: 'Searches per day',
      topCountries: 'Top countries',
      aiUsage: 'AI usage by model',
      noSearches: 'No searches in this period',
      open: 'Open',
      footnote: 'Days are in Tashkent time. Paid and trial accounts are counted as of now.',
    },
    range: { today: 'Today', d7: '7 days', d30: '30 days', d90: '90 days', from: 'From', to: 'To' },
    ai: {
      title: 'AI usage',
      subtitle: 'Gemini tokens used by the bot (voice and text requests, hotel names).',
      cost: 'Cost',
      costHint: 'Only keys marked as paid',
      tokens: 'Tokens',
      requestsHint: '{n} requests',
      input: 'Input',
      output: 'Output',
      outputHint: 'Answers + thinking',
      perDay: 'Tokens per day',
      keys: 'API keys',
      keysHint: 'Keys are shown by their last 4 characters. Turn on “Paid” for billed keys; free-tier keys cost $0.',
      key: 'Key',
      label: 'Name',
      paid: 'Paid',
      free: 'Free',
      removed: 'not in use',
      noKeys: 'No Gemini keys configured',
      models: 'Models and prices',
      pricesHint: 'USD per 1M tokens. Check current prices at ai.google.dev/pricing.',
      model: 'Model',
      priceIn: 'Input / 1M',
      priceOut: 'Output / 1M',
      noUsage: 'No AI usage in this period',
    },
    agents: {
      title: 'Agents',
      subtitle: 'Approve or block agency accounts. Payments are in Subscriptions.',
      search: 'Search',
      status: 'Status',
      approve: 'Approve',
      pending: 'Pending',
      block: 'Block',
      togglePlan: 'Toggle plan',
      delete: 'Delete agent',
      deleteConfirm: 'Are you sure you want to delete agent #{id}?',
      dialogTitle: 'Agent {id}',
      colId: 'ID',
      colAgency: 'Agency',
      colEmail: 'Email',
      colPhone: 'Phone',
      colStatus: 'Status',
      colPlan: 'Plan',
      colCreated: 'Created',
      colActions: 'Actions',
      statusPending: 'pending',
      statusActive: 'active',
      statusBlocked: 'blocked',
    },
    users: {
      title: 'All Users',
      subtitle: 'Unified user list (agents + consumers)',
      search: 'Search (name/email/phone/agency)',
      dialogTitle: 'User {id}',
      editTitle: 'Edit user',
      deleteTitle: 'Delete user',
      deleteConfirm: 'Are you sure you want to delete user #{id}?',
      fieldName: 'Name',
      fieldEmail: 'Email',
      fieldPhone: 'Phone',
      fieldAgency: 'Agency',
      fieldPassword: 'Password',
      passwordHint: 'Leave empty to keep unchanged',
      fieldLogoUrl: 'Logo URL',
      colId: 'ID',
      colName: 'Name',
      colEmail: 'Email',
      colPhone: 'Phone',
      colAgency: 'Agency',
      colPremium: 'Premium',
      colExpires: 'Expires (ms)',
      colPaidUntil: 'Paid until',
      colCreated: 'Created',
      colActions: 'Actions',
    },
    botUsers: {
      title: 'Bot Users',
      subtitle: 'Telegram bot user list',
      search: 'Search (name/phone)',
      dialogTitle: 'Bot user {id}',
      deleteTitle: 'Delete bot user',
      deleteConfirm: 'Are you sure you want to delete bot user #{id}?',
      colId: 'ID',
      colName: 'Name',
      colPhone: 'Phone',
      colChatId: 'Chat ID',
      colLang: 'Lang',
      colStep: 'Step',
      colCreated: 'Created',
      colActions: 'Actions',
    },
    providers: {
      title: 'Providers',
      subtitle: 'Enable/disable operators and set priority',
      colKey: 'Key',
      colLabel: 'Label',
      colEnabled: 'Enabled',
      colPriority: 'Priority',
      colUpdated: 'Updated',
    },
    curatedTours: {
      title: 'Author tours & Umrah',
      subtitle: 'Hand-entered packages shown to agents in the "Avtorlik turlar" and "Umra" sections',
      create: 'Add tour',
      createTitle: 'New tour',
      editTitle: 'Edit tour #{id}',
      deleteTitle: 'Delete tour?',
      deleteConfirm: 'Delete "{title}"? Agents will no longer see it. To hide it temporarily, switch it off instead.',
      section: { author: 'Author tours', group: 'Group tours', umra: 'Umrah' },
      colPoster: 'Poster',
      colSection: 'Section',
      colUpcoming: 'Next departure',
      noUpcoming: 'No upcoming dates',
      add: 'Add',
      nextWeek: '+1 week',
      nextWeekHint: 'Copy the last departure one week later with the same prices',
      pricesHint: 'Prices per person. QUAD/TRPL/DBL/SNGL by room type, or "Per person" for a single price. Past departures are hidden from agents automatically.',
      onePerLine: 'One item per line',
      sortHint: 'Lower first',
      noPoster: 'No poster',
      choosePoster: 'Choose poster',
      removePoster: 'Remove poster',
      auto: 'Auto-sync',
      syncedNotice: 'This tour is synced automatically from {operator} every few hours, so it can’t be edited here. You can still switch it on or off in the list.',
      posterHint: 'JPEG, PNG or WebP, up to 3 MB',
      price: { quad: 'QUAD', trpl: 'TRPL', dbl: 'DBL', sgl: 'SNGL', pp: 'Per person' },
      f: {
        section: 'Section', operator_name: 'Operator', title: 'Title', country: 'Country', cities: 'Cities',
        days: 'Days', nights: 'Nights', currency: 'Currency', sort_order: 'Order', is_active: 'Active',
        flight_info: 'Flights (one leg per line)', baggage: 'Baggage', hotels: 'Hotels', city: 'City', hotel: 'Hotel', meal: 'Meals',
        departures: 'Dates & prices', date_from: 'From', date_to: 'To', program: 'Program by day', day: 'Day', date: 'Date', text: 'Description',
        included: 'Included', excluded: 'Not included', notes: 'Notes', contacts: 'Operator contacts',
      },
    },
    subscriptions: {
      title: 'Subscriptions',
      subtitle: '{paid} paid · {trial} on trial · {total} accounts. A partner API key linked to an account shares its subscription.',
      search: 'Search company, email, phone',
      colAccount: 'Account',
      colStatus: 'Access',
      colPaidUntil: 'Paid until',
      colTrialUntil: 'Free until',
      colLastPayment: 'Last payment',
      colTotal: 'Total paid',
      colPartners: 'Partner keys',
      colActions: 'Actions',
      statusPaid: 'Paid',
      statusTrial: 'Free 3 days',
      statusExpired: 'Expired',
      statusTrialEnded: 'No access',
      addPayment: 'Add payment',
      history: 'Payment history',
      setTrial: 'Change free period',
      payTitle: 'Payment — {name}',
      amount: 'Amount',
      currency: 'Currency',
      startsOn: 'Paid from',
      endsOn: 'Paid until (last day)',
      months: '{n} mo',
      customEnd: 'Other end date',
      note: 'Note',
      payHint: 'Access lasts to the end of the last day (Tashkent time). Example: paid 20.10 for 1 month → works through 19.11.',
      historyTitle: 'Payments — {name}',
      noPayments: 'No payments yet.',
      period: 'Period',
      recorded: 'Recorded',
      trialTitle: 'Free access until',
    },
    partners: {
      title: 'Partners',
      subtitle: 'Partner list (paginated)',
      create: 'Create partner',
      createBtn: 'Create',
      dialogCreateTitle: 'Create partner',
      dialogEditTitle: 'Edit partner {id}',
      colAccount: 'Subscription account',
      linkAccount: 'Link to account',
      linkTitle: 'Partner {name}: whose subscription does it use?',
      linkHint: 'The API key works while that account has a paid subscription. Without a link the key gets its own 3 free days.',
      noAccount: 'Not linked (own 3 days)',
      dialogViewTitle: 'Partner {id}',
      dialogCreatedTitle: 'Partner created',
      deleteTitle: 'Delete partner',
      deleteConfirm: 'Delete partner #{id} ({name})? This cannot be undone.',
      colId: 'ID',
      colName: 'Name',
      colPhone: 'Phone',
      colIpAddress: 'IP address',
      ipCheck: 'Require IP whitelist',
      ipCheckHint: 'Turn off for partners on dynamic IPs: the API key alone authenticates them.',
      ipCheckOff: 'Any IP (key only)',
      colCreated: 'Created',
      colActions: 'Actions',
    },
    partnerAnalytics: {
      title: 'Partner analytics',
      subtitle: 'Requests, success and latency of the partner (B2B) API',
      subtitleUpdated: 'Partner (B2B) API · Updated {time}',
      allPartners: 'All partners',
      range_24h: '24h',
      range_7d: '7d',
      range_30d: '30d',
      range_90d: '90d',
      success: 'Success',
      failed: 'Failed',
      kpiRequests: 'Requests',
      kpiRequestsHint: '{n} active partners',
      kpiSuccessRate: 'Success rate',
      kpiSuccessHint: '{n} successful (2xx)',
      kpiFailed: 'Failed requests',
      kpiFailedHint: '{auth} auth · {server} server',
      kpiP95: 'p95 latency',
      kpiP95Hint: 'median {ms}',
      kpiSearches: 'Tour searches',
      kpiSearchesHint: '{n} tours returned',
      kpiEmpty: 'Empty results',
      kpiEmptyHint: 'of {n} tracked searches',
      kpiEmptyNone: 'no tracked searches yet',
      trafficTitle: 'Traffic',
      trafficHourly: 'Requests per hour (Tashkent time)',
      trafficDaily: 'Requests per day (Tashkent time)',
      mixTitle: 'Response mix',
      mixSubtitle: 'Outcome of every partner request',
      mixSuccess: 'Success',
      mixClient: 'Bad request',
      mixAuth: 'Auth failed',
      mixRate: 'Rate limited',
      mixServer: 'Server error',
      unidentified: '{n} requests had a missing or unknown API key and are not tied to a partner.',
      partnersTitle: 'Partners',
      partnersSubtitle: 'Click a partner to filter the whole page',
      colPartner: 'Partner',
      colRequests: 'Requests',
      colSuccessRate: 'Success',
      colFailed: 'Failed',
      colSearches: 'Searches',
      colEmpty: 'Empty',
      colLatency: 'Avg / p95',
      colLastSeen: 'Last request',
      colEndpoint: 'Endpoint',
      colTime: 'Time',
      colStatus: 'Status',
      colMessage: 'Error',
      colDuration: 'Duration',
      never: 'never',
      countriesTitle: 'Top destinations',
      countriesSubtitle: 'Most searched countries',
      avgTours: '~{n} tours',
      endpointsTitle: 'Endpoints',
      errorsTitle: 'Recent errors',
      errorsSubtitle: 'Latest 25 failed requests in the selected period',
      unknownPartner: 'Unknown key',
      noData: 'No requests in this period',
      noPartners: 'No partners yet',
      noErrors: 'No errors in this period',
    },
    searchHistory: {
      title: 'Search History',
      subtitle: 'Search logs across all users',
      filterUserId: 'Filter by user_id (optional)',
      dialogTitle: 'Search history {id}',
      colId: 'ID',
      colUserId: 'User ID',
      colUser: 'User',
      colAgency: 'Agency',
      colPhone: 'Phone',
      colCountry: 'Country',
      colCity: 'City',
      colFrom: 'From',
      colTo: 'To',
      colNights: 'Nights',
      colAdults: 'Adults',
      colKids: 'Kids',
      colCreated: 'Created',
      colActions: 'Actions',
    },
    broadcast: {
      title: 'Broadcast',
      subtitle: 'Send a message to bot users (queue)',
      messageLabel: 'Message text (HTML allowed: <b>, <i>, <a>)',
      targetLang: 'Target language',
      attach: 'Attach file',
      optionalMedia: 'Optional image/video',
      selectedFile: 'Selected: {name}',
      queue: 'Queue broadcast',
    },
    admins: {
      title: 'Admins',
      subtitle: 'Admin accounts (requires SUPER_ADMIN for create/edit/delete)',
      create: 'Create admin',
      dialogCreateTitle: 'Create admin',
      role: 'Role',
      roleViewer: 'VIEWER',
      roleAdmin: 'ADMIN',
      roleSuperAdmin: 'SUPER_ADMIN',
      colId: 'ID',
      colUsername: 'Username',
      colRole: 'Role',
      colCreated: 'Created',
      colActions: 'Actions',
      createBtn: 'Create',
    },
    recordDialog: {
      overview: 'Overview',
      json: 'JSON',
      noPrimitives: 'No primitive fields to show.',
    },
    userPremium: {
      title: 'Update premium',
      user: 'User: {id}{name}',
      nameSep: ' • ',
      expiredDate: 'Expired date',
      helperMs: 'Will be saved as Unix ms: {ms}',
      helperEmpty: 'Empty clears the expiry date',
      monthlyAmount: 'monthly_amount (integer)',
      paidMsg: 'paid_msg (optional)',
      unpaidReason: 'unpaid_reason (optional)',
      labelPremium: 'premium',
      labelDuration: 'duration',
      labelUsedFree: 'used_free',
    },
  },
  ru: {
    common: {
      refresh: 'Обновить',
      view: 'Просмотр',
      premium: 'Премиум',
      edit: 'Изменить',
      delete: 'Удалить',
      confirm: 'Подтвердить',
      cancel: 'Отмена',
      close: 'Закрыть',
      save: 'Сохранить',
      copyJson: 'Копировать JSON',
      yes: 'Да',
      no: 'Нет',
      all: 'Все',
      signingIn: 'Вход…',
      creating: 'Создание…',
      saving: 'Сохранение…',
      queueing: 'В очередь…',
      result: 'Результат',
    },
    theme: { light: 'Светлая', dark: 'Тёмная' },
    language: { en: 'English', ru: 'Русский' },
    botLang: { all: 'Все', uz: 'Узбекский', ru: 'Русский', en: 'Английский' },
    nav: {
      dashboard: 'Панель',
      agents: 'Агенты',
      users: 'Все пользователи',
      botUsers: 'Пользователи бота',
      providers: 'Провайдеры',
      partners: 'Партнёры',
      subscriptions: 'Подписки',
      curatedTours: 'Туры',
      searchHistory: 'История поиска',
      broadcast: 'Рассылка',
      admins: 'Администраторы',
      aiUsage: 'Расход ИИ',
      language: 'Язык',
      logout: 'Выйти',
      partnerAnalytics: 'Аналитика партнёров',
      sectionOverview: 'Обзор',
      sectionPeople: 'Пользователи',
      sectionOperations: 'Операции',
      sectionSystem: 'Система',
    },
    time: { justNow: 'только что', minAgo: '{n} мин назад', hourAgo: '{n} ч назад', dayAgo: '{n} дн назад' },
    layout: { appName: 'UMA Admin' },
    login: {
      title: 'UMA Admin',
      subtitle: 'Войдите с учётными данными администратора',
      username: 'Имя пользователя',
      password: 'Пароль',
      signIn: 'Войти',
      sideTitle: 'Всё, на чём работает UMA, в одном месте',
      sideText: 'Агенты, партнёры, операторы, туры и пакеты Умры.',
    },
    errors: { generic: 'Что-то пошло не так' },
    notFound: {
      title: 'Страница не найдена',
      body: 'Запрашиваемая страница не существует.',
      cta: 'На панель',
    },
    dashboard: {
      title: 'Панель',
      searches: 'Поиски',
      searchersHint: 'искали {n} польз.',
      newAccounts: 'Новые аккаунты',
      newAccountsHint: '{agents} агентов · {bot} в боте',
      paidAccounts: 'Платные аккаунты',
      paidHint: '{trial} на пробном · получено {revenue}',
      aiCost: 'Расход на ИИ',
      aiHint: '{tokens} токенов',
      searchesPerDay: 'Поиски по дням',
      topCountries: 'Популярные страны',
      aiUsage: 'ИИ по моделям',
      noSearches: 'Нет поисков за период',
      open: 'Открыть',
      footnote: 'Дни по времени Ташкента. Платные и пробные аккаунты — на текущий момент.',
    },
    range: { today: 'Сегодня', d7: '7 дней', d30: '30 дней', d90: '90 дней', from: 'С', to: 'По' },
    ai: {
      title: 'Расход ИИ',
      subtitle: 'Токены Gemini, использованные ботом (голос и текст, названия отелей).',
      cost: 'Стоимость',
      costHint: 'Только платные ключи',
      tokens: 'Токены',
      requestsHint: '{n} запросов',
      input: 'Вход',
      output: 'Выход',
      outputHint: 'Ответы + размышления',
      perDay: 'Токены по дням',
      keys: 'API-ключи',
      keysHint: 'Ключи показаны по последним 4 символам. Включите «Платный» для оплачиваемых ключей; бесплатные стоят $0.',
      key: 'Ключ',
      label: 'Название',
      paid: 'Платный',
      free: 'Бесплатный',
      removed: 'не используется',
      noKeys: 'Ключи Gemini не настроены',
      models: 'Модели и цены',
      pricesHint: 'USD за 1M токенов. Актуальные цены: ai.google.dev/pricing.',
      model: 'Модель',
      priceIn: 'Вход / 1M',
      priceOut: 'Выход / 1M',
      noUsage: 'Нет расхода ИИ за период',
    },
    agents: {
      title: 'Агенты',
      subtitle: 'Одобрение и блокировка агентств. Оплаты — в разделе «Подписки».',
      search: 'Поиск',
      status: 'Статус',
      approve: 'Одобрить',
      pending: 'На модерации',
      block: 'Заблокировать',
      togglePlan: 'Сменить план',
      delete: 'Удалить агента',
      deleteConfirm: 'Удалить агента #{id}?',
      dialogTitle: 'Агент {id}',
      colId: 'ID',
      colAgency: 'Агентство',
      colEmail: 'Email',
      colPhone: 'Телефон',
      colStatus: 'Статус',
      colPlan: 'План',
      colCreated: 'Создан',
      colActions: 'Действия',
      statusPending: 'ожидание',
      statusActive: 'активен',
      statusBlocked: 'заблокирован',
    },
    users: {
      title: 'Все пользователи',
      subtitle: 'Общий список (агенты и клиенты)',
      search: 'Поиск (имя/email/телефон/агентство)',
      dialogTitle: 'Пользователь {id}',
      editTitle: 'Редактировать пользователя',
      deleteTitle: 'Удалить пользователя',
      deleteConfirm: 'Удалить пользователя #{id}?',
      fieldName: 'Имя',
      fieldEmail: 'Email',
      fieldPhone: 'Телефон',
      fieldAgency: 'Агентство',
      fieldPassword: 'Пароль',
      passwordHint: 'Оставьте пустым, чтобы не менять',
      fieldLogoUrl: 'Logo URL',
      colId: 'ID',
      colName: 'Имя',
      colEmail: 'Email',
      colPhone: 'Телефон',
      colAgency: 'Агентство',
      colPremium: 'Премиум',
      colExpires: 'Истекает (мс)',
      colPaidUntil: 'Оплачено до',
      colCreated: 'Создан',
      colActions: 'Действия',
    },
    botUsers: {
      title: 'Пользователи бота',
      subtitle: 'Список пользователей Telegram-бота',
      search: 'Поиск (имя/телефон)',
      dialogTitle: 'Пользователь бота {id}',
      deleteTitle: 'Удалить пользователя бота',
      deleteConfirm: 'Удалить пользователя бота #{id}?',
      colId: 'ID',
      colName: 'Имя',
      colPhone: 'Телефон',
      colChatId: 'Chat ID',
      colLang: 'Язык',
      colStep: 'Шаг',
      colCreated: 'Создан',
      colActions: 'Действия',
    },
    providers: {
      title: 'Провайдеры',
      subtitle: 'Включение/выключение и приоритет',
      colKey: 'Ключ',
      colLabel: 'Название',
      colEnabled: 'Вкл.',
      colPriority: 'Приоритет',
      colUpdated: 'Обновлён',
    },
    curatedTours: {
      title: 'Авторские туры и Умра',
      subtitle: 'Пакеты, введённые вручную; агенты видят их в разделах «Авторские туры» и «Умра»',
      create: 'Добавить тур',
      createTitle: 'Новый тур',
      editTitle: 'Тур #{id}',
      deleteTitle: 'Удалить тур?',
      deleteConfirm: 'Удалить «{title}»? Агенты больше не увидят его. Чтобы скрыть временно, просто выключите.',
      section: { author: 'Авторские туры', group: 'Групповые туры', umra: 'Умра' },
      colPoster: 'Постер',
      colSection: 'Раздел',
      colUpcoming: 'Ближайший выезд',
      noUpcoming: 'Нет будущих дат',
      add: 'Добавить',
      nextWeek: '+1 неделя',
      nextWeekHint: 'Скопировать последний выезд на неделю позже с теми же ценами',
      pricesHint: 'Цены за человека. QUAD/TRPL/DBL/SNGL по типу размещения или «За чел.» — одна цена. Прошедшие даты скрываются от агентов автоматически.',
      onePerLine: 'По одному пункту в строке',
      sortHint: 'Меньше — выше',
      noPoster: 'Нет постера',
      choosePoster: 'Выбрать постер',
      removePoster: 'Удалить постер',
      auto: 'Авто',
      syncedNotice: 'Этот тур автоматически синхронизируется с {operator} каждые несколько часов, поэтому здесь его не изменить. Включить или выключить можно в списке.',
      posterHint: 'JPEG, PNG или WebP, до 3 МБ',
      price: { quad: 'QUAD', trpl: 'TRPL', dbl: 'DBL', sgl: 'SNGL', pp: 'За чел.' },
      f: {
        section: 'Раздел', operator_name: 'Оператор', title: 'Название', country: 'Страна', cities: 'Города',
        days: 'Дней', nights: 'Ночей', currency: 'Валюта', sort_order: 'Порядок', is_active: 'Активен',
        flight_info: 'Перелёты (по одному на строку)', baggage: 'Багаж', hotels: 'Отели', city: 'Город', hotel: 'Отель', meal: 'Питание',
        departures: 'Даты и цены', date_from: 'С', date_to: 'По', program: 'Программа по дням', day: 'День', date: 'Дата', text: 'Описание',
        included: 'Включено', excluded: 'Не включено', notes: 'Примечания', contacts: 'Контакты оператора',
      },
    },
    subscriptions: {
      title: 'Подписки',
      subtitle: '{paid} оплачено · {trial} на пробном · {total} аккаунтов. Ключ партнёра, привязанный к аккаунту, использует его подписку.',
      search: 'Компания, email, телефон',
      colAccount: 'Аккаунт',
      colStatus: 'Доступ',
      colPaidUntil: 'Оплачено до',
      colTrialUntil: 'Бесплатно до',
      colLastPayment: 'Последний платёж',
      colTotal: 'Всего оплачено',
      colPartners: 'Ключи партнёра',
      colActions: 'Действия',
      statusPaid: 'Оплачено',
      statusTrial: '3 дня бесплатно',
      statusExpired: 'Истекла',
      statusTrialEnded: 'Нет доступа',
      addPayment: 'Добавить оплату',
      history: 'История оплат',
      setTrial: 'Изменить бесплатный период',
      payTitle: 'Оплата — {name}',
      amount: 'Сумма',
      currency: 'Валюта',
      startsOn: 'Оплачено с',
      endsOn: 'Оплачено до (последний день)',
      months: '{n} мес',
      customEnd: 'Другая дата',
      note: 'Комментарий',
      payHint: 'Доступ до конца последнего дня (по Ташкенту). Пример: оплатили 20.10 на 1 месяц → работает до 19.11 включительно.',
      historyTitle: 'Оплаты — {name}',
      noPayments: 'Оплат пока нет.',
      period: 'Период',
      recorded: 'Записано',
      trialTitle: 'Бесплатный доступ до',
    },
    partners: {
      title: 'Партнёры',
      subtitle: 'Список партнёров (постранично)',
      create: 'Создать партнёра',
      createBtn: 'Создать',
      dialogCreateTitle: 'Создать партнёра',
      dialogEditTitle: 'Редактировать партнёра {id}',
      colAccount: 'Аккаунт подписки',
      linkAccount: 'Привязать к аккаунту',
      linkTitle: 'Партнёр {name}: чью подписку использует?',
      linkHint: 'API-ключ работает, пока у этого аккаунта есть оплаченная подписка. Без привязки у ключа свои 3 бесплатных дня.',
      noAccount: 'Не привязан (свои 3 дня)',
      dialogViewTitle: 'Партнёр {id}',
      dialogCreatedTitle: 'Партнёр создан',
      deleteTitle: 'Удалить партнёра',
      deleteConfirm: 'Удалить партнёра #{id} ({name})? Это действие нельзя отменить.',
      colId: 'ID',
      colName: 'Имя',
      colPhone: 'Телефон',
      colIpAddress: 'IP-адрес',
      ipCheck: 'Проверять IP (whitelist)',
      ipCheckHint: 'Выключите для партнёров с динамическим IP: доступ только по API-ключу.',
      ipCheckOff: 'Любой IP (только ключ)',
      colCreated: 'Создан',
      colActions: 'Действия',
    },
    partnerAnalytics: {
      title: 'Аналитика партнёров',
      subtitle: 'Запросы, успешность и задержка партнёрского (B2B) API',
      subtitleUpdated: 'Партнёрский (B2B) API · Обновлено {time}',
      allPartners: 'Все партнёры',
      range_24h: '24ч',
      range_7d: '7д',
      range_30d: '30д',
      range_90d: '90д',
      success: 'Успешно',
      failed: 'Ошибка',
      kpiRequests: 'Запросы',
      kpiRequestsHint: 'активных партнёров: {n}',
      kpiSuccessRate: 'Успешность',
      kpiSuccessHint: 'успешных (2xx): {n}',
      kpiFailed: 'Ошибочные запросы',
      kpiFailedHint: 'авторизация {auth} · сервер {server}',
      kpiP95: 'Задержка p95',
      kpiP95Hint: 'медиана {ms}',
      kpiSearches: 'Поиски туров',
      kpiSearchesHint: 'туров отдано: {n}',
      kpiEmpty: 'Пустые результаты',
      kpiEmptyHint: 'из {n} учтённых поисков',
      kpiEmptyNone: 'учтённых поисков пока нет',
      trafficTitle: 'Трафик',
      trafficHourly: 'Запросы по часам (время Ташкента)',
      trafficDaily: 'Запросы по дням (время Ташкента)',
      mixTitle: 'Результаты запросов',
      mixSubtitle: 'Итог каждого запроса партнёров',
      mixSuccess: 'Успешно',
      mixClient: 'Неверный запрос',
      mixAuth: 'Ошибка авторизации',
      mixRate: 'Превышен лимит',
      mixServer: 'Ошибка сервера',
      unidentified: 'Запросов без ключа или с неизвестным ключом: {n} (не привязаны к партнёру).',
      partnersTitle: 'Партнёры',
      partnersSubtitle: 'Нажмите на партнёра, чтобы отфильтровать страницу',
      colPartner: 'Партнёр',
      colRequests: 'Запросы',
      colSuccessRate: 'Успешно',
      colFailed: 'Ошибки',
      colSearches: 'Поиски',
      colEmpty: 'Пустые',
      colLatency: 'Сред. / p95',
      colLastSeen: 'Последний запрос',
      colEndpoint: 'Эндпоинт',
      colTime: 'Время',
      colStatus: 'Статус',
      colMessage: 'Ошибка',
      colDuration: 'Длительность',
      never: 'никогда',
      countriesTitle: 'Популярные направления',
      countriesSubtitle: 'Самые искомые страны',
      avgTours: '~{n} туров',
      endpointsTitle: 'Эндпоинты',
      errorsTitle: 'Последние ошибки',
      errorsSubtitle: 'Последние 25 ошибочных запросов за период',
      unknownPartner: 'Неизвестный ключ',
      noData: 'Нет запросов за этот период',
      noPartners: 'Партнёров пока нет',
      noErrors: 'Ошибок за этот период нет',
    },
    searchHistory: {
      title: 'История поиска',
      subtitle: 'Журнал поисков по всем пользователям',
      filterUserId: 'Фильтр по user_id (необязательно)',
      dialogTitle: 'Запись истории {id}',
      colId: 'ID',
      colUserId: 'User ID',
      colUser: 'Пользователь',
      colAgency: 'Агентство',
      colPhone: 'Телефон',
      colCountry: 'Страна',
      colCity: 'Город',
      colFrom: 'С',
      colTo: 'По',
      colNights: 'Ночей',
      colAdults: 'Взрослых',
      colKids: 'Детей',
      colCreated: 'Создано',
      colActions: 'Действия',
    },
    broadcast: {
      title: 'Рассылка',
      subtitle: 'Отправить сообщение пользователям бота (очередь)',
      messageLabel: 'Текст сообщения (HTML: <b>, <i>, <a>)',
      targetLang: 'Язык получателей',
      attach: 'Прикрепить файл',
      optionalMedia: 'Необязательно изображение/видео',
      selectedFile: 'Выбрано: {name}',
      queue: 'Поставить в очередь',
    },
    admins: {
      title: 'Администраторы',
      subtitle: 'Учётные записи (создание/удаление — только SUPER_ADMIN)',
      create: 'Создать админа',
      dialogCreateTitle: 'Создать админа',
      role: 'Роль',
      roleViewer: 'VIEWER',
      roleAdmin: 'ADMIN',
      roleSuperAdmin: 'SUPER_ADMIN',
      colId: 'ID',
      colUsername: 'Имя',
      colRole: 'Роль',
      colCreated: 'Создан',
      colActions: 'Действия',
      createBtn: 'Создать',
    },
    recordDialog: {
      overview: 'Обзор',
      json: 'JSON',
      noPrimitives: 'Нет простых полей для отображения.',
    },
    userPremium: {
      title: 'Премиум-подписка',
      user: 'Пользователь: {id}{name}',
      nameSep: ' • ',
      expiredDate: 'Дата окончания',
      helperMs: 'Будет сохранено как Unix ms: {ms}',
      helperEmpty: 'Пусто — сброс даты',
      monthlyAmount: 'monthly_amount (целое)',
      paidMsg: 'paid_msg (необязательно)',
      unpaidReason: 'unpaid_reason (необязательно)',
      labelPremium: 'premium',
      labelDuration: 'duration',
      labelUsedFree: 'used_free',
    },
  },
};
